import { makeErrorWithCode } from '@neo-one/utils';
import BigNumber from 'bignumber.js';
import BN from 'bn.js';
export const InvalidFormatError = makeErrorWithCode('INVALID_FORMAT', (reason) => `Invalid format${reason === undefined ? '.' : `: ${reason}`}`);
const add0x = (value) => (value.startsWith('0x') ? value : `0x${value}`);
const strip0x = (value) => (value.startsWith('0x') ? value.substring(2) : value);
const reverse = (src) => {
    const mutableOut = Buffer.allocUnsafe(src.length);
    for (let i = 0, j = src.length - 1; i <= j; i += 1, j -= 1) {
        mutableOut[i] = src[j];
        mutableOut[j] = src[i];
    }
    return mutableOut;
};
const UINT160_BUFFER_BYTES = 20;
const isUInt160 = (value) => value instanceof Buffer && value.length === UINT160_BUFFER_BYTES;
const asUInt160 = (value) => {
    if (isUInt160(value)) {
        return value;
    }
    throw new InvalidFormatError('Invalid UInt160');
};
const bufferToUInt160 = asUInt160;
const uInt160ToHex = (value) => add0x(reverse(value).toString('hex'));
const hexToUInt160 = (value) => typeof value === 'string' ? asUInt160(reverse(Buffer.from(strip0x(value), 'hex'))) : asUInt160(value);
const uInt160ToBuffer = (value) => typeof value === 'string' ? hexToUInt160(value) : value;
const uInt160ToString = (value) => typeof value === 'string' ? value : uInt160ToHex(value);
const stringToUInt160 = hexToUInt160;
const uInt160Equal = (a, b) => a.equals(b);
const ZERO_UINT160 = Buffer.alloc(20, 0);
const UINT256_BUFFER_BYTES = 32;
const ZERO_UINT256 = Buffer.alloc(UINT256_BUFFER_BYTES, 0);
const isUInt256 = (value) => value instanceof Buffer && value.length === UINT256_BUFFER_BYTES;
const asUInt256 = (value) => {
    if (isUInt256(value)) {
        return value;
    }
    throw new InvalidFormatError('Invalid UInt256');
};
const bufferToUInt256 = asUInt256;
const uInt256ToHex = (value) => add0x(reverse(value).toString('hex'));
const hexToUInt256 = (value) => typeof value === 'string' ? bufferToUInt256(reverse(Buffer.from(strip0x(value), 'hex'))) : bufferToUInt256(value);
const uInt256ToBuffer = (value) => typeof value === 'string' ? hexToUInt256(value) : value;
const uInt256ToString = (value) => typeof value === 'string' ? value : uInt256ToHex(value);
const stringToUInt256 = hexToUInt256;
const uInt256Equal = (a, b) => a.equals(b);
const toUInt32LE = (bytes) => new BN(uInt256ToBuffer(bytes).slice(0, 4), 'le').toNumber();
const ECPOINT_BUFFER_BYTES = 33;
const ECPOINT_INFINITY_BYTE = 0x00;
const ECPOINT_INFINITY = Buffer.from([ECPOINT_INFINITY_BYTE]);
const isECPoint = (value) => value instanceof Buffer && (value.length === ECPOINT_BUFFER_BYTES || value.equals(ECPOINT_INFINITY));
const asECPoint = (value) => {
    if (isECPoint(value)) {
        return value;
    }
    throw new InvalidFormatError('Invalid ECPoint');
};
const bufferToECPoint = asECPoint;
const ecPointToHex = (value) => typeof value === 'string' ? value : value.toString('hex');
const hexToECPoint = (value) => bufferToECPoint(typeof value === 'string' ? Buffer.from(value, 'hex') : value);
const ecPointToBuffer = (value) => typeof value === 'string' ? hexToECPoint(value) : value;
const ecPointToString = (value) => typeof value === 'string' ? value : ecPointToHex(value);
const stringToECPoint = hexToECPoint;
const ecPointEqual = (a, b) => a.equals(b);
const ecPointCompare = (a, b) => {
    const aHex = ecPointToHex(a);
    const bHex = ecPointToHex(b);
    if (aHex < bHex) {
        return -1;
    }
    if (aHex > bHex) {
        return 1;
    }
    return 0;
};
const ecPointIsInfinity = (value) => value.equals(ECPOINT_INFINITY);
const PRIVATE_KEY_BUFFER_BYTES = 32;
const isPrivateKey = (value) => value instanceof Buffer && value.length === PRIVATE_KEY_BUFFER_BYTES;
const asPrivateKey = (value) => {
    if (isPrivateKey(value)) {
        return value;
    }
    throw new InvalidFormatError(`Invalid Private Key, found: ${value}`);
};
const bufferToPrivateKey = asPrivateKey;
const privateKeyToHex = (value) => typeof value === 'string' ? value : value.toString('hex');
const hexToPrivateKey = (value) => bufferToPrivateKey(typeof value === 'string' ? Buffer.from(strip0x(value), 'hex') : value);
const privateKeyToBuffer = (value) => typeof value === 'string' ? hexToPrivateKey(value) : value;
const privateKeyToString = (value) => typeof value === 'string' ? value : privateKeyToHex(value);
const stringToPrivateKey = hexToPrivateKey;
const fixedFromDecimal = (value, decimals) => {
    if (value instanceof BN) {
        return value;
    }
    const d = new BN(10 ** decimals);
    if (typeof value === 'number') {
        return new BN(value).mul(d);
    }
    const valueBigNumber = typeof value === 'string' ? new BigNumber(value) : value;
    const dBigNumber = new BigNumber(d.toString(10));
    return new BN(valueBigNumber.times(dBigNumber).toString(), 10);
};
const D8 = new BN(100000000);
const fixed8FromDecimal = (value) => fixedFromDecimal(value, 8);
const fixedToDecimal = (value, decimals) => {
    const dBigNumber = new BigNumber(10 ** decimals);
    return new BigNumber(value.toString(10)).div(dBigNumber);
};
const fixed8ToDecimal = (bn) => fixedToDecimal(bn, 8);
const NEGATIVE_SATOSHI_FIXED8 = new BN(-1);
const TEN_FIXED8 = fixed8FromDecimal(10);
const ONE_HUNDRED_FIXED8 = fixed8FromDecimal(100);
const FOUR_HUNDRED_FIXED8 = fixed8FromDecimal(400);
const FIVE_HUNDRED_FIXED8 = fixed8FromDecimal(500);
const ONE_THOUSAND_FIXED8 = fixed8FromDecimal(1000);
const FIVE_THOUSAND_FIXED8 = fixed8FromDecimal(5000);
const TEN_THOUSAND_FIXED8 = fixed8FromDecimal(10000);
const ONE_HUNDRED_MILLION_FIXED8 = fixed8FromDecimal(100000000);
export const common = {
    D8,
    NEO_ADDRESS_VERSION: 23,
    NEO_PRIVATE_KEY_VERSION: 0x80,
    ECPOINT_BUFFER_BYTES,
    ECPOINT_INFINITY,
    ECPOINT_INFINITY_BYTE,
    PRIVATE_KEY_BUFFER_BYTES,
    UINT160_BUFFER_BYTES,
    UINT256_BUFFER_BYTES,
    ZERO_UINT160,
    ZERO_UINT256,
    NEGATIVE_SATOSHI_FIXED8,
    TEN_FIXED8,
    ONE_HUNDRED_FIXED8,
    FOUR_HUNDRED_FIXED8,
    FIVE_HUNDRED_FIXED8,
    ONE_THOUSAND_FIXED8,
    FIVE_THOUSAND_FIXED8,
    TEN_THOUSAND_FIXED8,
    ONE_HUNDRED_MILLION_FIXED8,
    NEO_ASSET_HASH: '0xc56f33fc6ecfcd0c225c4ab356fee59390af8560be0e930faebe74a6daff7c9b',
    GAS_ASSET_HASH: '0x602c79718b16e442de58778e148d0b1084e3b2dffd5de6b7b16cee7969282de7',
    uInt160ToBuffer,
    add0x,
    strip0x,
    asUInt160,
    uInt160ToHex,
    hexToUInt160,
    uInt160ToString,
    stringToUInt160,
    uInt160Equal,
    isUInt160,
    bufferToUInt160,
    isUInt256,
    asUInt256,
    isECPoint,
    asECPoint,
    uInt256ToBuffer,
    bufferToUInt256,
    uInt256ToHex,
    hexToUInt256,
    uInt256ToString,
    stringToUInt256,
    uInt256Equal,
    toUInt32LE,
    ecPointToBuffer,
    bufferToECPoint,
    ecPointToHex,
    hexToECPoint,
    ecPointToString,
    ecPointCompare,
    stringToECPoint,
    ecPointEqual,
    ecPointIsInfinity,
    privateKeyToHex,
    hexToPrivateKey,
    privateKeyToBuffer,
    bufferToPrivateKey,
    privateKeyToString,
    stringToPrivateKey,
    isPrivateKey,
    asPrivateKey,
    fixed8FromDecimal,
    fixed8ToDecimal,
    fixedFromDecimal,
    fixedToDecimal,
    reverse,
};

//# sourceMappingURL=common.js.map
